import { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';

interface LocationSearchProps {
  onLocationSelect: (lat: number, lon: number, name: string) => void;
}

interface Suggestion {
  place_name: string;
  center: [number, number];
}

export const MAPBOX_TOKEN =
  'pk.eyJ1IjoidGluYXVra2VsaSIsImEiOiJjbTNvY3o0Y3kwMXVqMm1zZTVna254OGV1In0.IljVd6sN8o__c0yKzoAs7g';

export function LocationSearch({ onLocationSelect }: LocationSearchProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?` +
            new URLSearchParams({
              access_token: MAPBOX_TOKEN,
              types: 'place',
              limit: '5',
            }),
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  return (
    <div className="relative w-full" ref={searchRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          type="text"
          placeholder="Search location..."
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-pink-500/20 dark:focus:ring-pink-400/20 focus:border-pink-500/20 dark:focus:border-pink-400/20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
          data-ph-capture-attribute="input-location-search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowSuggestions(true);
          }}
        />
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg rounded-md py-1 border border-gray-200 dark:border-gray-700">
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.place_name}
              className="w-full px-4 py-2 text-left hover:bg-pink-50 dark:hover:bg-pink-900/20 hover:text-pink-700 dark:hover:text-pink-300 text-sm text-gray-900 dark:text-gray-100 transition-colors"
              onClick={() => {
                onLocationSelect(suggestion.center[1], suggestion.center[0], suggestion.place_name);
                setSearchQuery(suggestion.place_name);
                setShowSuggestions(false);
              }}
              data-ph-capture-attribute="btn-location-search-suggestion"
            >
              {suggestion.place_name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
